import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import { BasicModalProps } from '@swarm/core/hooks/state/usePopupState'
import useEnableAsset from '@swarm/core/hooks/web3/useEnableAsset'
import { useAllowanceOf } from '@swarm/core/observables/allowanceOf'
import { getCurrentConfig } from '@swarm/core/observables/configForNetwork'
import { TRANSACTION_RELOAD_DELAY } from '@swarm/core/shared/consts'
import { getInputPrecision } from '@swarm/core/shared/utils/tokens/precision'
import { useAccount } from '@swarm/core/web3'
import { ExtendedSecurity } from '@swarm/types/normalized-entities'
import Dialog from '@swarm/ui/presentational/Dialog'
import Divider from '@swarm/ui/presentational/Divider'
import SmartButton from '@swarm/ui/swarm/Buttons/SmartButton'
import TokenBalance from '@swarm/ui/swarm/TokenBalance'
import TokenIcon from '@swarm/ui/swarm/TokenIcon'
import { TransactionResult } from 'contract-proxy-kit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Flex, Text } from 'rimble-ui'
import styled from 'styled-components/macro'

import useSecurities from 'src/hooks/staking/useSecurities'
import useEnterStaking from 'src/hooks/web3/useEnterStaking'

import StakeDetailsList from './StakeDetailsList'
import StakeSecurityInput from './StakeSecurityInput'

const InfoText = styled(Text)`
  text-align: center;
  line-height: 1.2;
`

export interface SuccessStakeValue {
  stakedAmount: number
  securityToken: ExtendedSecurity
  tx: TransactionResult
}

interface StakeSecurityPopupProps extends BasicModalProps {
  securityToken: ExtendedSecurity | null
  onSuccess?(value: SuccessStakeValue): void
}

const { swarmStakingAddress } = getCurrentConfig()

const StakeSecurityPopup = ({
  isOpen,
  onClose,
  onSuccess,
  securityToken = null,
}: StakeSecurityPopupProps) => {
  const { securities, loading: securitiesLoading } = useSecurities()
  const { t } = useTranslation(['staking', 'errors'])

  const account = useAccount()
  const { nativeSmt } = useNativeSmt()
  const allowance = useAllowanceOf(account, swarmStakingAddress, nativeSmt?.id)

  const [amount, setAmount] = useState(0)
  const [selectedToken, setSelectedToken] = useState<ExtendedSecurity | null>(
    securityToken,
  )

  useEffect(() => {
    setSelectedToken(securityToken)

    return () => {
      setAmount(0)
    }
  }, [securityToken])

  const [enterStaking, { txLoading: txStakeLoading }] = useEnterStaking()

  const handleStake = useCallback(async () => {
    if (!selectedToken?.id || !amount) return

    try {
      const tx = (await enterStaking(
        selectedToken,
        amount,
      )) as TransactionResult

      if (tx) {
        setTimeout(() => {
          onClose?.()
          onSuccess?.({
            securityToken: selectedToken,
            stakedAmount: amount,
            tx,
          })
        }, TRANSACTION_RELOAD_DELAY)
      }
    } catch {}
  }, [selectedToken, amount, enterStaking, onClose, onSuccess])

  const [enableToken, { txLoading: txEnableLoading }] = useEnableAsset()

  const handleConfirmButtonClick = useCallback(async () => {
    if (nativeSmt && nativeSmt.type && allowance?.lt(amount)) {
      return enableToken(nativeSmt, swarmStakingAddress, nativeSmt.type)
    }

    return handleStake()
  }, [nativeSmt, allowance, amount, handleStake, enableToken])

  const { isValid, error: validationError } = useMemo(() => {
    const zeroAmount = amount === 0

    return {
      isValid: !zeroAmount,
      error: zeroAmount && t('errors:amountMoreThan', { amount: 0 }),
    }
  }, [amount, t])

  const disabled = !isValid || securitiesLoading
  const loading = txStakeLoading || txEnableLoading

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      width={['100%', '550px']}
      minHeight="450px"
      maxHeight={['90%', '80%']}
      height="auto"
    >
      <Flex alignItems="center" mb={3}>
        <TokenIcon symbol={nativeSmt?.symbol} height="25px" maxWidth="25px" />
        <Text fontSize={4} fontWeight={5} ml={2} color="primary">
          {t('stakeSecurityPopup.title')}
        </Text>
      </Flex>
      <StakeDetailsList />

      <Flex flexDirection="column">
        <Flex justifyContent="space-between" mb={1}>
          <Text color="near-black">{t('stakeSecurityPopup.stakeAgainst')}</Text>
          <Flex wrap="nowrap" alignItems="center">
            <Text color="near-black" mr={1}>
              {t('stakeSecurityPopup.balance')}:
            </Text>
            <Box>
              <TokenBalance
                account={account}
                tokenAddress={nativeSmt?.id}
                symbol={nativeSmt?.symbol}
              />
            </Box>
          </Flex>
        </Flex>

        <StakeSecurityInput
          value={amount}
          min={0}
          tokens={securities}
          error={validationError || ''}
          max={nativeSmt?.balance?.toNumber() || 0}
          decimalScale={getInputPrecision({
            symbol: nativeSmt?.symbol || '',
          })}
          onTokenChange={setSelectedToken}
          onAmountChange={setAmount}
          endAdornment={nativeSmt?.symbol}
          selectedToken={selectedToken}
          tokensLoading={false}
        />
      </Flex>

      <Divider my="20px" />

      <Text fontSize={3} color="primary" textAlign="center" mb="12px">
        {t('stakingDetails.youAreStaking', {
          amount,
          symbol: selectedToken?.symbol,
        })}
      </Text>

      <Flex justifyContent="center" mb="12px">
        <Button.Outline
          width="70px"
          height="36px"
          borderColor="primary"
          border="1.5px solid"
          fontWeight={4}
          onClick={onClose}
        >
          {t('stakingDetails.backBtn')}
        </Button.Outline>

        <SmartButton
          requireInitiated
          requireAccount
          type="button"
          width="106px"
          height="36px"
          ml={2}
          loading={loading}
          disabled={disabled}
          onClick={handleConfirmButtonClick}
        >
          {allowance?.lt(amount)
            ? t('stakingDetails.enableToken')
            : t('stakingDetails.stakeBtn')}
        </SmartButton>
      </Flex>

      <Flex flexDirection="column">
        <InfoText fontSize={1} color="near-black">
          {t('stakingDetails.earningInfo')}
        </InfoText>
        <InfoText fontSize={1} color="near-black">
          {t('stakingDetails.removeInfo')}
        </InfoText>
      </Flex>
    </Dialog>
  )
}

export default StakeSecurityPopup
